<template>
  <v-container fluid>
    <template v-if="isDashboardDeprecated">
      <v-alert type="warning"
               prominent
               class="mb-8">
        The Team Dashboard has been replaced by <a href="https://metabase.mobilelocker.com" target="_blank" class="white--text">Metabase</a>. Contact Mobile Locker support (support@mobilelocker.com) if you want to enable Metabase.
      </v-alert>
    </template>

    <template v-else>
      <v-alert type="warning"
               prominent
               class="mb-8">
        The Team Dashboard is being replaced by <a href="https://metabase.mobilelocker.com" target="_blank" class="white--text">Metabase</a>, our reporting platform. This dashboard will be removed by {{ deprecationDate.format('MMMM, D, YYYY')}}. Contact Mobile Locker support (support@mobilelocker.com) if you want to enable Metabase.
      </v-alert>
      <v-form @submit.prevent=""
              :disabled="loading"
              class="mb-4">
        <v-card>
          <v-card-text :class="loading ? 'py-0' : 'pt-0 pb-1'">
            <v-row>
              <v-col cols="3">
                <v-select v-model="form.range"
                          label="Time Period"
                          placeholder=" "
                          :items="dateRanges"
                          item-value="id"
                          item-text="name"
                          hide-details
                          @change="updateDateRange">
                </v-select>
              </v-col>
              <v-col cols="12"
                     md="3">
                <v-menu ref="startMenu"
                        v-model="startMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                        z-index="999">
                  <template v-slot:activator="{on, attrs}">
                    <v-text-field v-model="form.start"
                                  label="From"
                                  prepend-icon="fal fa-calendar"
                                  readonly
                                  hide-details
                                  v-bind="attrs"
                                  v-on="on">
                    </v-text-field>
                  </template>

                  <v-date-picker v-model="form.start"
                                 :min="minStartDate"
                                 :max="maxStartDate"
                                 @input="form.range = 'custom'; startMenu = false; endMenu = true;">
                  </v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12"
                     md="3">

                <v-menu ref="endMenu"
                        v-model="endMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                        z-index="999">
                  <template v-slot:activator="{on, attrs}">
                    <v-text-field v-model="form.end"
                                  label="To"
                                  prepend-icon="fal fa-calendar"
                                  readonly
                                  hide-details
                                  v-bind="attrs"
                                  v-on="on">
                    </v-text-field>
                  </template>

                  <v-date-picker v-model="form.end"
                                 :min="minEndDate"
                                 :max="maxEndDate"
                                 @input="form.range = 'custom'; startMenu = false; endMenu = false; fetchData()">
                  </v-date-picker>
                </v-menu>

              </v-col>
              <v-col cols="12"
                     md="3"
                     class="d-flex align-center">
                <v-btn type="submit"
                       color="primary"
                       :disabled="loading"
                       @click.prevent="fetchData">
                  Apply
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <v-progress-linear indeterminate
                             v-if="loading"
                             color="primary">
          </v-progress-linear>
        </v-card>
      </v-form>
      <template v-if="loading">

        <v-row>
          <v-col v-for="n in 6"
                 cols="12"
                 md="6"
                 :key="n">
            <v-skeleton-loader type="card">
            </v-skeleton-loader>
          </v-col>
        </v-row>

        <v-skeleton-loader type="table"
                           class="mb-4">
        </v-skeleton-loader>

        <v-skeleton-loader type="table"
                           class="mb-4">
        </v-skeleton-loader>
      </template>
      <template v-else>

        <v-row>

          <v-col cols="12"
                 md="6">
            <sharing-activity-chart :loading="loading"
                                    :metrics="teamMetrics"
                                    palette="palette1">
            </sharing-activity-chart>
          </v-col>

          <v-col cols="12"
                 md="6">
            <content-activity-chart :loading="loading"
                                    :metrics="teamMetrics"
                                    palette="palette1">
            </content-activity-chart>
          </v-col>

        </v-row>

        <v-row>
          <v-col cols="12"
                 md="6">
            <sharing-heatmap-chart :loading="loading"
                                   :metrics="teamHeatMetrics"
                                   palette="palette2"
                                   period="day_of_week"
                                   title="Sharing by Week Day"
                                   x-axis-title="Day of Week">
            </sharing-heatmap-chart>
          </v-col>

          <v-col cols="12"
                 md="6"
                 v-if="teamCharts">
            <content-activity-heatmap-chart :loading="loading"
                                            :metrics="teamHeatMetrics"
                                            palette="palette2"
                                            period="day_of_week"
                                            title="Content Viewing by Week Day"
                                            x-axis-title="Day of Week">
            </content-activity-heatmap-chart>
          </v-col>
        </v-row>

        <v-row>

          <v-col cols="12"
                 md="6"
                 v-if="teamCharts">
            <users-chart :loading="loading"
                         :metrics="teamMetrics"
                         palette="palette3">
            </users-chart>
          </v-col>

          <v-col cols="12"
                 md="6"
                 v-if="teamCharts">
            <disk-usage-chart :loading="loading"
                              :metrics="teamMetrics"
                              title="Storage Space"
                              palette="palette2">
            </disk-usage-chart>
          </v-col>

        </v-row>

        <presentation-activity-table :loading="loading"
                                     :metrics="presentationMetrics"
                                     class="mb-4">
        </presentation-activity-table>

        <user-activity-table :loading="loading"
                             :metrics="userMetrics"
                             class="mb-4">
        </user-activity-table>

        <group-activity-table :loading="loading"
                              :metrics="groupMetrics"
                              class="mb-4">
        </group-activity-table>

        <region-activity-table :loading="loading"
                               :metrics="regionMetrics"
                               class="mb-4">
        </region-activity-table>


      </template>
    </template>
  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import moment from 'moment-timezone';
import DiskUsageChart from '../../components/charts/DiskUsageChart';
import PresentationsChart from '../../components/charts/PresentationsChart';
import TeamActivityChart from '../../components/charts/TeamActivityChart';
import UsersChart from '../../components/charts/UsersChart';
import SharingActivityChart from '../../components/charts/SharingActivityChart';
import PresentationTypePieChart from '../../components/charts/PresentationTypePieChart';
import PresentationActivityTable from '../../components/charts/PresentationActivityTable';
import ContentActivityChart from '../../components/charts/ContentActivityChart';
import UserActivityTable from '../../components/charts/UserActivityTable';
import RegionActivityTable from '../../components/charts/RegionActivityTable';
import GroupActivityTable from '../../components/charts/GroupActivityTable';
import find from "lodash/find";
import SharingHeatmapChart from '../../components/charts/SharingHeatmapChart';
import ContentActivityHeatmapChart from "../../components/charts/ContentActivityHeatmapChart";

export default {
  components: {ContentActivityHeatmapChart, SharingHeatmapChart, GroupActivityTable, RegionActivityTable, UserActivityTable, ContentActivityChart, PresentationActivityTable, PresentationTypePieChart, SharingActivityChart, UsersChart, PresentationsChart, DiskUsageChart, TeamActivityChart},
  metaInfo() {
    this.$store.commit('setTitle', 'Team Dashboard');
    return {
      title: this.$store.getters.title,
    }
  },
  data() {
    return {
      error: null,
      loading: false,
      groupMetrics: [],
      presentationMetrics: [],
      regionMetrics: [],
      teamMetrics: [],
      userMetrics: [],
      teamHeatMetrics: [],
      typeMetrics: [],
      form: new Form({
        group_id: '',
        team_id: '',
        user_id: '',
        range: 'past_30',
        start: '',
        end: '',
      }),
      startDialog: false,
      endDialog: false,
      startMenu: false,
      endMenu: false,
      dateRanges: [
        {id: 'past_7', name: 'Past 7 days', start: moment().add(-7, 'day').startOf('day'), end: moment().add(-1, 'day').endOf('day')},
        {id: 'past_15', name: 'Past 15 days', start: moment().add(-15, 'day').startOf('day'), end: moment().add(-1, 'day').endOf('day')},
        {id: 'past_30', name: 'Past 30 days', start: moment().add(-30, 'day').startOf('day'), end: moment().add(-1, 'day').endOf('day')},
        {id: 'past_45', name: 'Past 45 days', start: moment().add(-45, 'day').startOf('day'), end: moment().add(-1, 'day').endOf('day')},
        {id: 'past_60', name: 'Past 60 days', start: moment().add(-60, 'day').startOf('day'), end: moment().add(-1, 'day').endOf('day')},
        {id: 'past_90', name: 'Past 90 days', start: moment().add(-90, 'day').startOf('day'), end: moment().add(-1, 'day').endOf('day')},
        {id: 'this_week', name: 'This week', start: moment().startOf('week'), end: moment().endOf('day')},
        {id: 'this_month', name: 'This month', start: moment().startOf('month'), end: moment().endOf('day')},
        {id: 'this_quarter', name: 'This quarter', start: moment().startOf('quarter'), end: moment().endOf('day')},
        {isDefault: true, id: 'this_year', name: 'This year', start: moment().startOf('year'), end: moment().endOf('day')},
        {id: 'last_week', name: 'Last week', start: moment().add(-1, 'week').startOf('week'), end: moment().add(-1, 'week').endOf('week')},
        {id: 'last_month', name: 'Last month', start: moment().add(-1, 'month').startOf('month'), end: moment().add(-1, 'month').endOf('month')},
        {id: 'last_quarter', name: 'Last quarter', start: moment().add(-1, 'quarter').startOf('quarter'), end: moment().add(-1, 'quarter').endOf('quarter')},
        {id: 'last_year', name: 'Last year', start: moment().add(-1, 'year').startOf('year'), end: moment().add(-1, 'year').endOf('year')},
        {isCustom: true, id: 'custom', name: 'Custom'},
      ],
    }
  },
  computed: {
    ...mapGetters([
      'groups',
      'team',
      'activeUsers',
    ]),
    users() {

    },
    defaultDateRange() {
      return find(this.dateRanges, r => r.isDefault);
    },
    minStartDate() {
      return moment(this.team.created_at).format('YYYY-MM-DD');
    },
    maxStartDate() {
      return moment().add(-1, 'day').format('YYYY-MM-DD')
    },
    minEndDate() {
      //return moment(this.form.startDate).format('YYYY-MM-DD')
      return moment(this.team.created_at).add(1, 'day').format('YYYY-MM-DD');
    },
    maxEndDate() {
      return moment().add(-1, 'day').format('YYYY-MM-DD')
    },
    groupCharts() {
      return !!this.form.group_id;
    },
    userCharts() {
      return !!this.form.user_id;
    },
    teamCharts() {
      return !this.groupCharts && !this.userCharts;
    },
    groupID() {
      if (this.$route.query.group_id) {
        return parseInt(this.$route.query.group_id);
      }
      return null;
    },
    teamID() {
      if (this.$route.query.team_id) {
        return parseInt(this.$route.query.team_id);
      }
      return this.team.id;
    },
    userID() {
      if (this.$route.query.user_id) {
        return parseInt(this.$route.query.user_id);
      }
      return null;
    },
    deprecationDate() { // MLD-5085
      return moment.utc('2023-08-31')
    },
    isDashboardDeprecated() { // MLD-5085
      return moment.utc().isAfter(this.deprecationDate);
    }
  },
  created() {
    if (this.isDashboardDeprecated === false) {
      this.initData();
      this.initForm();
      this.fetchData();
    }
  },
  methods: {
    initData() {
      this.$store.dispatch('fetchGroups');
      this.$store.dispatch('fetchUsers');
    },
    initForm() {
      this.initDates();
      this.form.group_id = this.groupID ? this.groupID : '';
      this.form.team_id = this.teamID ? this.teamID : this.team.id;
      this.form.user_id = this.userID ? this.userID : '';
    },
    initDates() {
      if (this.defaultDateRange) {
        this.form.range = this.defaultDateRange.id;
        this.form.start = this.defaultDateRange.start.format('YYYY-MM-DD');
        this.form.end = this.defaultDateRange.end.format('YYYY-MM-DD');
      }
    },
    resetForm() {
      this.initDates();
      this.form.group_id = '';
      this.form.user_id = '';
      this.fetchData();
    },
    selectGroup() {
      this.form.user_id = '';
      this.fetchData();
    },
    selectUser() {
      this.form.group_id = '';
      this.fetchData();
    },
    async fetchData() {
      if (this.loading) {
        return;
      }
      try {
        this.loading = true;
        const params = {
          group_id: this.form.group_id,
          team_id: this.form.team_id,
          user_id: this.form.user_id,
          start: this.form.start,
          end: this.form.end,
        }

        await Promise.all([
          axios.get(`/api/metrics/team`, {params}).then(response => this.teamMetrics = response.data),
          axios.get(`/api/metrics/groups`, {params}).then(response => this.groupMetrics = response.data),
          axios.get(`/api/metrics/presentations`, {params}).then(response => this.presentationMetrics = response.data),
          axios.get(`/api/metrics/regions`, {params}).then(response => this.regionMetrics = response.data),
          axios.get(`/api/metrics/users`, {params}).then(response => this.userMetrics = response.data),
          axios.get(`/api/metrics/team/heatmap/day_of_week`, {params}).then(response => this.teamHeatMetrics = response.data),
        ])

        if (this.teamCharts) {
          const {data: typeMetrics} = await axios.get(`/api/metrics/presentations/type`, {params})
          this.typeMetrics = typeMetrics;
        }
      } catch (err) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    updateDateRange() {
      const dateRange = find(this.dateRanges, r => r.id === this.form.range);
      if (dateRange) {
        if (dateRange.isCustom) {
          console.log('selected custom');
          this.startMenu = true;
          this.endMenu = false;
        } else {
          this.form.start = dateRange.start.format('YYYY-MM-DD');
          this.form.end = dateRange.end.format('YYYY-MM-DD');
          this.fetchData();
        }
      }
    }
  }
}
</script>
